"use client";

import React from "react";
import { X } from "lucide-react";
import { cn } from "@/ui/cn";
import { Card, CardHeader, CardTitle, CardContent } from "@/ui/card";
import { Button } from "@/ui/button";
import { useUIStore } from "@/lib/stores/ui";

export const FloatingPanel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    title: string;
    defaultX?: number;
    defaultY?: number;
    width?: number;
    centered?: boolean;
    persistId?: string;
    onClose?: () => void;
    onPositionChange?: (x: number, y: number) => void;
    contentClassName?: string;
    headerActions?: React.ReactNode;
  }
>(
  (
    {
      title,
      defaultX = 20,
      defaultY = 80,
      width = 400,
      centered = false,
      persistId,
      onClose,
      onPositionChange,
      contentClassName,
      headerActions,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const panelId = React.useMemo(
      () => (persistId ? persistId : title.toLowerCase().replace(/\s+/g, "-")),
      [persistId, title]
    );

    const storedPosition = useUIStore((state) => state.getFloatingPanelPosition(panelId));
    const setStoredPosition = useUIStore((state) => state.setFloatingPanelPosition);

    const cardRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        cardRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    const [position, setPosition] = React.useState({ x: defaultX, y: defaultY });
    const [hasManualPosition, setHasManualPosition] = React.useState(false);
    const [isDragging, setIsDragging] = React.useState(false);
    const dragOffsetRef = React.useRef({ x: 0, y: 0 });

    // Hydrate a stored (previously dragged) position after client mount.
    React.useEffect(() => {
      if (storedPosition) {
        setPosition(storedPosition);
        setHasManualPosition(true);
      }
    }, [storedPosition]);

    // Until the user drags (or a stored position exists), a `centered` panel uses CSS
    // margin-auto centering so it re-centers naturally on resize; dragging switches to px.
    const useCenteredCss = centered && !hasManualPosition;

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      let startX = position.x;
      let startY = position.y;
      if (useCenteredCss && cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        startX = rect.left;
        startY = rect.top;
        setPosition({ x: startX, y: startY });
      }
      setHasManualPosition(true);
      setIsDragging(true);
      dragOffsetRef.current = { x: e.clientX - startX, y: e.clientY - startY };
    };

    React.useEffect(() => {
      if (!isDragging) return;

      const handleMouseMove = (e: MouseEvent) => {
        const newX = e.clientX - dragOffsetRef.current.x;
        const newY = e.clientY - dragOffsetRef.current.y;
        const newPosition = { x: newX, y: newY };
        setPosition(newPosition);
        setStoredPosition(panelId, newPosition);
        onPositionChange?.(newX, newY);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }, [isDragging, onPositionChange, panelId, setStoredPosition]);

    return (
      <Card
        ref={setRefs}
        className={cn(
          "z-30 pointer-events-auto bg-background",
          useCenteredCss ? "fixed" : "absolute",
          className
        )}
        style={
          useCenteredCss
            ? {
                left: 0,
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
                marginInline: "auto",
                width: `${width}px`,
                minHeight: "200px",
                maxHeight: "80vh",
              }
            : {
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: `${width}px`,
                minHeight: "200px",
                maxHeight: "80vh",
              }
        }
        {...props}
      >
        <CardHeader
          onMouseDown={handleMouseDown}
          className="cursor-move select-none flex flex-row items-center justify-between gap-2"
        >
          <CardTitle>{title}</CardTitle>
          {(headerActions || onClose) && (
            <div className="flex items-center gap-1 shrink-0">
              {headerActions}
              {onClose && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={onClose}
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className={cn("overflow-auto", contentClassName)}>{children}</CardContent>
      </Card>
    );
  }
);

FloatingPanel.displayName = "FloatingPanel";
